import _ from "lodash";
import { useState } from "react";
import { useNavigate } from "react-router";

import { useIsMobile } from "hooks/use-is-mobile";
import { useDirtyFormState } from "hooks/use-dirty-form-state";
import MKLoader from "components/loader/MKLoader";
import FileUploadErrorModal from "components/shared/fileUploadErrorModal/FileUploadErrorModal";
import useSetErrorOnUpload from "hooks/use-set-error-on-upload";
import { Attachment } from "models/dashboard/dashboard";
import { DocumentUploadFile } from "models/DocumentUploadFile";
import { useRootStore } from "store";
import DiscardMessageModal from "views/inbox/DiscardMessageModal";
import CreateThreadDesktop from "./createThread/desktop";
import CreateThreadMobile from "./createThread/mobile";

type File = {
  id: string;
  name: string;
  type: string;
};

export default function MessageEditor() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { isDirty, markDirty, clearDirty } = useDirtyFormState();

  const { global, user, archive, inbox } = useRootStore((store) => store);

  const { sendFileToArchive } = archive;

  const { fetchThread, loadInbox, sendMessage, activeFilters } = inbox;

  const [isSending, setIsSending] = useState(false);
  const [showConfirmDiscardModal, setShowConfirmDiscardModal] = useState(false);

  const { failed, setFailed, failMessage, setFailedWrapper } =
    useSetErrorOnUpload();

  const hasActiveFilters = (): boolean => {
    const { flagged, unread, drafts, archived, categories } = activeFilters;

    return flagged || unread || drafts || archived || categories.length > 0;
  };

  const handleOnSend = async (
    title: string,
    body: string,
    recipients: string[],
    attachments: Attachment[],
    filesToUpload: DocumentUploadFile[],
    type?: string,
    reportStart?: string,
    reportEnd?: string,
    category?: number
  ) => {
    setIsSending(true);

    try {
      const workingCustomerId = _.clone(global.workingCustomerId);

      const result = (await sendFileToArchive(
        filesToUpload,
        workingCustomerId,
        "Inbox",
        setFailedWrapper
      )) as unknown;
      if (!result) {
        setIsSending(false);
        return;
      }
      const files = result as File[];
      let uploadedAttachments: File[] = [];
      if (files && files.length > 0) {
        uploadedAttachments = files.map((file) => {
          return { id: file.id, name: file.name, type: file.type };
        });
      }

      const allAttachments = [...attachments, ...uploadedAttachments];

      const sendMessageResult = (await sendMessage(
        user.userInfo?.id ?? "",
        body,
        title,
        null,
        recipients,
        workingCustomerId,
        allAttachments,
        type,
        reportStart,
        reportEnd,
        category
      )) as any;

      setIsSending(false);
      loadInbox();

      if (sendMessageResult) {
        clearDirty();
        fetchThread(global.workingCustomerId, sendMessageResult.threadId);
        if (hasActiveFilters()) {
          navigate("/inbox/my");
        } else {
          navigate(
            `/inbox/my/thread/${encodeURIComponent(sendMessageResult.threadId)}`
          );
        }
      }
    } catch (error) {
      setIsSending(false);
      console.error("Error sending message or uploading files:", error);
    }
  };

  const handleDiscardClicked = () => {
    if (isDirty) {
      setShowConfirmDiscardModal(true);
      return;
    }
    navigate(-1);
  };
  const handleConfirmDiscard = () => {
    clearDirty();
    setShowConfirmDiscardModal(false);
    navigate(-1);
  };

  return (
    <div
      className={`inbox-content-wrapper inbox-content-wrapper-new-message d-flex w-100`}
    >
      {failed && (
        <FileUploadErrorModal failMessage={failMessage} setFailed={setFailed} />
      )}

      {isSending && !failed && (
        <MKLoader show={isSending} message="Skickar meddelande..." />
      )}

      {showConfirmDiscardModal && (
        <DiscardMessageModal
          onDismiss={() => setShowConfirmDiscardModal(false)}
          onCancel={() => setShowConfirmDiscardModal(false)}
          onConfirm={handleConfirmDiscard}
        />
      )}

      {isMobile ? (
        <CreateThreadMobile
          isSending={isSending}
          showSubjectLine
          showBackIcon
          defaultRecipients={[]}
          onSend={handleOnSend}
          onDiscardClicked={handleDiscardClicked}
          markDirty={markDirty}
          clearDirty={clearDirty}
        />
      ) : (
        <CreateThreadDesktop
          isSending={isSending}
          showSubjectLine
          defaultRecipients={[]}
          onSend={handleOnSend}
          onDiscardClicked={handleDiscardClicked}
          markDirty={markDirty}
          clearDirty={clearDirty}
        />
      )}
    </div>
  );
}
